import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, 
  Heart, 
  CreditCard, 
  Shield, 
  Copy, 
  Check,
  QrCode,
  Smartphone,
  Building2,
  Mail,
  Coffee,
  BookOpen,
  Server,
  Sparkles,
  Zap,
  Upload,
  Clock,
  CheckCircle,
  XCircle,
  Image
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DonationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState({ title: 'Faça uma Doação', content: '' });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedAI, setCopiedAI] = useState(false);
  
  // Upload state
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [myRequests, setMyRequests] = useState([]);

  // PIX Data
  const PIX_KEY = 'charleshenriquevoos@gmail.com';
  const PIX_NAME = 'Charles Henrique Voos';
  const PIX_BANK = 'Nubank';

  useEffect(() => {
    fetchContent();
    if (user) {
      fetchMyRequests();
    }
  }, [user]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/content/donation_info`);
      setContent(response.data);
    } catch (error) {
      console.error('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRequests = async () => {
    try {
      const response = await api.get('/api/donations/my-requests');
      setMyRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch my requests');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor, selecione uma imagem');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Imagem muito grande (máximo 5MB)');
        return;
      }
      setProofFile(file);
      setProofPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmitProof = async () => {
    if (!proofFile) {
      toast.error('Por favor, selecione o comprovante');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('amount', '3.99');
      formData.append('proof_image', proofFile);

      await api.post('/api/donations/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Comprovante enviado! Aguarde aprovação em até 24 horas.');
      setProofFile(null);
      setProofPreview(null);
      fetchMyRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar comprovante');
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center gap-1 text-amber-600 bg-amber-100 px-2 py-1 rounded-full text-xs"><Clock className="h-3 w-3" /> Aguardando</span>;
      case 'approved':
        return <span className="inline-flex items-center gap-1 text-green-600 bg-green-100 px-2 py-1 rounded-full text-xs"><CheckCircle className="h-3 w-3" /> Aprovado</span>;
      case 'rejected':
        return <span className="inline-flex items-center gap-1 text-red-600 bg-red-100 px-2 py-1 rounded-full text-xs"><XCircle className="h-3 w-3" /> Rejeitado</span>;
      default:
        return null;
    }
  };

  const copyPixKey = () => {
    navigator.clipboard.writeText(PIX_KEY);
    setCopied(true);
    toast.success('Chave PIX copiada!');
    setTimeout(() => setCopied(false), 3000);
  };

  const copyPixKeyForAI = () => {
    navigator.clipboard.writeText(PIX_KEY);
    setCopiedAI(true);
    toast.success('Chave PIX copiada! Doe R$ 3,99 e envie o comprovante para receber seus créditos.');
    setTimeout(() => setCopiedAI(false), 3000);
  };

  const impactItems = [
    { icon: Server, title: 'Servidores', desc: 'Manter a plataforma online 24/7' },
    { icon: Sparkles, title: 'IA e Busca', desc: 'Melhorar a busca semântica inteligente' },
    { icon: BookOpen, title: 'Conteúdo', desc: 'Ampliar o banco de chamadas científicas' },
    { icon: Coffee, title: 'Desenvolvimento', desc: 'Criar novas funcionalidades' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F9F8] to-white flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="h-6 w-6 text-red-500 fill-red-500" />
            <h1 className="text-2xl font-bold tracking-tight">Apoie o Projeto</h1>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-6 py-8 w-full">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Hero Section */}
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-500 to-pink-500 rounded-full mb-4 shadow-lg">
                <Heart className="h-10 w-10 text-white fill-white" />
              </div>
              <h2 className="text-3xl font-bold text-[#0A0A0A] mb-3">Ajude a Ciência Brasileira</h2>
              <p className="text-lg text-[#4B4C52] max-w-2xl mx-auto">
                O <strong>Quero Publicar</strong> é gratuito para todos os pesquisadores. 
                Sua doação mantém a plataforma viva e ajuda milhares de acadêmicos.
              </p>
            </div>

            {/* Monthly Cost Info */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 text-center">
              <p className="text-green-800 text-lg">
                <strong>Nosso custo mensal é de apenas R$ 100</strong>
              </p>
              <p className="text-green-700 text-sm mt-2">
                Este projeto é mantido 100% por voluntários. Com sua ajuda, conseguimos manter o site no ar e continuar ajudando pesquisadores de todo o Brasil.
              </p>
            </div>

            {/* AI Credits Offer - Only for logged users */}
            {user && (
              <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-8 text-white shadow-xl">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="bg-white/20 p-3 rounded-full">
                    <Zap className="h-8 w-8" />
                  </div>
                  <h3 className="text-2xl font-bold">Busca com IA</h3>
                </div>

                <div className="text-center mb-6">
                  <div className="inline-block bg-white/20 backdrop-blur-sm rounded-xl px-6 py-4 mb-4">
                    <p className="text-4xl font-bold">R$ 3,99</p>
                    <p className="text-white/80">= 5 buscas com IA</p>
                  </div>
                  <p className="text-white/90">
                    Use inteligência artificial para encontrar as chamadas mais compatíveis com sua pesquisa!
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Sparkles className="h-5 w-5" />
                    Como funciona:
                  </h4>
                  <ol className="text-sm text-white/90 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="bg-white/20 rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
                      <span>Faça um PIX de <strong>R$ 3,99</strong> para a chave abaixo</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-white/20 rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
                      <span>Envie o comprovante para <strong>quero.publicar@queropublicar.org</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-white/20 rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
                      <span>Receba seus <strong>5 créditos</strong> em até 24 horas!</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-white rounded-xl p-4 text-gray-900">
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    <Mail className="h-4 w-4 inline mr-1" />
                    Chave PIX (E-mail)
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 rounded-lg px-4 py-3 font-mono text-sm break-all">
                      {PIX_KEY}
                    </div>
                    <Button
                      onClick={copyPixKeyForAI}
                      className={`${copiedAI ? 'bg-green-500 hover:bg-green-600' : 'bg-purple-600 hover:bg-purple-700'} transition-colors`}
                    >
                      {copiedAI ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copiar
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Seu email cadastrado: <strong>{user.email}</strong>
                  </p>
                </div>

                {/* Upload Proof Section */}
                <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Enviar Comprovante
                  </h4>
                  
                  {myRequests.some(r => r.status === 'pending') ? (
                    <div className="bg-amber-100 text-amber-800 rounded-lg p-3 text-sm">
                      <Clock className="h-4 w-4 inline mr-1" />
                      Você já tem um comprovante aguardando aprovação. Por favor, aguarde.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <label className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                          <div className="border-2 border-dashed border-white/30 rounded-lg p-4 text-center cursor-pointer hover:border-white/50 transition-colors">
                            {proofPreview ? (
                              <img src={proofPreview} alt="Comprovante" className="max-h-32 mx-auto rounded" />
                            ) : (
                              <>
                                <Image className="h-8 w-8 mx-auto mb-2 text-white/60" />
                                <p className="text-sm text-white/80">Clique para selecionar o comprovante</p>
                              </>
                            )}
                          </div>
                        </label>
                      </div>
                      
                      {proofFile && (
                        <Button
                          onClick={handleSubmitProof}
                          disabled={uploading}
                          className="w-full bg-white text-purple-700 hover:bg-white/90"
                        >
                          {uploading ? (
                            <>
                              <div className="animate-spin h-4 w-4 border-2 border-purple-700 border-t-transparent rounded-full mr-2" />
                              Enviando...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Enviar Comprovante
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* My Requests History */}
                {myRequests.length > 0 && (
                  <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                    <h4 className="font-semibold mb-3 text-sm">Minhas Solicitações</h4>
                    <div className="space-y-2">
                      {myRequests.slice(0, 3).map((req) => (
                        <div key={req.donation_id} className="flex items-center justify-between bg-white/10 rounded-lg p-2 text-sm">
                          <span>R$ {req.amount?.toFixed(2)}</span>
                          {getStatusBadge(req.status)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Login prompt for non-logged users */}
            {!user && (
              <div className="bg-gradient-to-br from-purple-100 to-indigo-100 border border-purple-200 rounded-xl p-6 text-center">
                <Zap className="h-10 w-10 text-purple-600 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-purple-900 mb-2">Busca com IA</h3>
                <p className="text-purple-700 mb-4">
                  Faça login para desbloquear a busca inteligente com IA por apenas <strong>R$ 3,99</strong> (5 buscas).
                </p>
                <Button 
                  onClick={() => navigate('/auth')}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Fazer Login
                </Button>
              </div>
            )}

            {/* Impact Section */}
            <div className="bg-white border border-[#E4E4E7] rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-center mb-6">Sua doação ajuda a:</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {impactItems.map((item, index) => (
                  <div key={index} className="text-center p-4 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors">
                    <item.icon className="h-8 w-8 mx-auto mb-2 text-[#002FA7]" />
                    <p className="font-semibold text-sm">{item.title}</p>
                    <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* PIX Section - Main CTA */}
            <div className="bg-gradient-to-br from-[#002FA7] to-[#0040D0] rounded-2xl p-8 text-white shadow-xl">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="bg-white/20 p-3 rounded-full">
                  <QrCode className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-bold">Doe via PIX</h3>
              </div>
              
              <p className="text-center text-white/90 mb-6">
                O PIX é instantâneo, seguro e sem taxas. Escaneie o QR Code ou copie a chave abaixo.
              </p>

              <div className="bg-white rounded-xl p-6 text-gray-900">
                {/* PIX Key */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    <Mail className="h-4 w-4 inline mr-1" />
                    Chave PIX (E-mail)
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 rounded-lg px-4 py-3 font-mono text-sm break-all">
                      {PIX_KEY}
                    </div>
                    <Button
                      onClick={copyPixKey}
                      className={`${copied ? 'bg-green-500 hover:bg-green-600' : 'bg-[#002FA7] hover:bg-[#001f75]'} transition-colors`}
                    >
                      {copied ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copiar
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Bank Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-500 text-xs mb-1">Favorecido</p>
                    <p className="font-medium">{PIX_NAME}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-500 text-xs mb-1">
                      <Building2 className="h-3 w-3 inline mr-1" />
                      Banco
                    </p>
                    <p className="font-medium text-purple-600">{PIX_BANK}</p>
                  </div>
                </div>

                {/* Instructions */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 flex items-start gap-2">
                    <Smartphone className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Abra o app do seu banco, vá em PIX, cole a chave e escolha o valor que desejar.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* PayPal Section */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-bold text-blue-900">Doe via PayPal</h3>
              </div>
              
              <p className="text-blue-800 mb-6">
                Prefere cartão de crédito ou débito? Use o PayPal para doar com segurança.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4">
                <form action="https://www.paypal.com/donate" method="post" target="_top">
                  <input type="hidden" name="business" value="4PRRSD87NPF3U" />
                  <input type="hidden" name="no_recurring" value="0" />
                  <input type="hidden" name="item_name" value="Doação para o QueroPublicar.org" />
                  <input type="hidden" name="currency_code" value="BRL" />
                  <input 
                    type="image" 
                    src="https://www.paypalobjects.com/pt_BR/BR/i/btn/btn_donateCC_LG.gif" 
                    border="0" 
                    name="submit" 
                    title="PayPal - Doe com segurança" 
                    alt="Doar com PayPal"
                    className="hover:opacity-90 transition-opacity cursor-pointer"
                    data-testid="paypal-donate-btn"
                  />
                </form>

                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Shield className="h-4 w-4" />
                  <span>Pagamento seguro e protegido</span>
                </div>
              </div>
            </div>

            {/* Other Ways */}
            <div className="bg-white border border-[#E4E4E7] rounded-xl p-8">
              <h3 className="text-lg font-bold mb-6 text-center">Outras formas de apoiar</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors">
                  <span className="text-3xl">📢</span>
                  <div>
                    <strong className="block text-[#0A0A0A]">Divulgue</strong>
                    <p className="text-sm text-gray-600">Compartilhe com colegas pesquisadores</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 hover:bg-green-50 transition-colors">
                  <span className="text-3xl">📝</span>
                  <div>
                    <strong className="block text-[#0A0A0A]">Contribua</strong>
                    <p className="text-sm text-gray-600">Sugira novas chamadas para publicação</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 hover:bg-yellow-50 transition-colors">
                  <span className="text-3xl">💡</span>
                  <div>
                    <strong className="block text-[#0A0A0A]">Feedback</strong>
                    <p className="text-sm text-gray-600">Envie sugestões de melhorias</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 hover:bg-purple-50 transition-colors">
                  <span className="text-3xl">⭐</span>
                  <div>
                    <strong className="block text-[#0A0A0A]">Avalie</strong>
                    <p className="text-sm text-gray-600">Deixe uma avaliação positiva</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Thank You */}
            <div className="text-center py-8">
              <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 px-6 py-3 rounded-full">
                <Heart className="h-5 w-5 fill-red-500" />
                <span className="font-medium">Obrigado por apoiar a ciência brasileira!</span>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default DonationPage;
